import bridge from '@/plugins/vue-constructor-bridge';
import { v4 as uuid } from 'uuid';

import convertToOrderItem from '@/helpers/convert-to-order-item';
import formatCurrency from '@/helpers/format/format-currency';
import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import http from '@/services/http';
import logger from '@/services/logger';
import moment from 'moment-timezone';
import normalizePaymentRequestBody from '@/helpers/normalize-payment-request-body';
import resolveCloverPaymentActions from '@/helpers/resolve-clover-payment-actions';
import parseCapiError from '@/helpers/parse-capi-error';
import parseGroupOrderDetails from '@/helpers/parse-group-order-details';
import parseOrderItem from '@/helpers/parse-order-item';
import parseUserLocationType from '@/helpers/parse-user-location-type';
import parseUpsellItems from '@/helpers/parse-upsell-items';
import mapToFulfillmentType from '@/helpers/map-to-fulfillment-type';
import getLocationValidations from '../helpers/get-location-validations';
import storage from '@/services/storage';
import {
  getItemsExceedingInventory,
  getInvalidDaypartItems,
  getUnavailableItemsAndModifiers
} from '@/helpers/unavailable-items';



export default {
  namespaced: true,

  state: {
    cachedOrderIdSelection: null,
    menuDate: null,
    order: {
      applePassUrl: null,
      attachmentUrl: null,
      discount: 0,
      email: null,
      externalOrderId: null,
      externalPaymentAmount: 0,
      features: null,
      fulfillmentType: null,
      groupOrder: null,
      googleWalletJwt: null,
      isAddingItem: false,
      isAssigning: false,
      isLoading: false,
      locationType: null,
      locationTypeDescription: null,
      loyaltyEngagementSummary: null,
      menuType: null,
      offers: [],
      orderAttributes: {},
      orderFees: [],
      orderFeeTotal: 0,
      orderId: null,
      orderLevelInstructions: null,
      orderItems: [],
      orderItemsStaged: [],
      orderItemTotal: 0,
      orderState: null,
      orderType: null,
      payments: [],
      phoneNumber: null,
      posOrderStatus: null,
      preparationTime: null,
      removingItemIds: [],
      removingOfferIds: [],
      serviceCharges: 0,
      showRedemptionQrCode: false,
      status: null,
      store: {
        address: {}
      },
      storeFees: 0,
      subtotal: 0,
      tableNumber: null,
      tableNumberDisplayName: null,
      tax: 0,
      ticketNumber: null,
      total: 0,
      totalWithTipAndCharges: 0,
      upsellItems: [],
      upsellRules: [],
      upsellDisplayMode: null,
      tdsData: null,
      cloverBrowserInfo: null,
      orderUpdateKey: ''
    },
    orderErrors: [],
    orders: [],
    orderMenuTypeIsUpdating: false
  },

  getters: {
    currentCapiOrderType(state, getters, rootState, rootGetters) {
      const orderDetails = rootGetters['session/orderDetails'];
      const session = rootGetters['session/session'];
      let type = 'OrderAhead';

      if (orderDetails.orderType === 'pay') {
        if ([7, 8].includes(session.route.channelTypeId)) {
          type = 'TextToPay';
        }
        else {
          type = 'PayAtTable';
        }
      }
      else if (orderDetails.orderType === 'table') {
        type = 'OrderAtTable';
      }
      else if (orderDetails.orderType === 'tip-only') {
        type = 'TipOnly';
      }

      return type;
    },

    fulfillmentEstimateTimestamp(state, getters, rootState, rootGetters) {
      const orderDetails = rootGetters['session/orderDetails'];
      const selectedLocation = rootGetters['locations/selectedLocation'];

      if (orderDetails.fulfillmentType === 'delivery') {
        return orderDetails.deliveryEstimate.deliveryTime;
      }

      const asapTime = moment(selectedLocation.asapPickupTimestamp);
      const selectedTime = moment(orderDetails.fulfillmentTime);

      if (selectedTime.diff(asapTime) > 0) {
        return orderDetails.fulfillmentTime;
      }

      return selectedLocation.asapPickupTimestamp;
    },

    groupOrder(state, getters, rootState, rootGetters) {
      const groupOrder = parseGroupOrderDetails({
        guestId: rootGetters['session/orderDetails'].groupMemberGuestId,
        merchantId: rootState.session.route.merchantId,
        order: state.order
      });

      return groupOrder;
    },

    order(state, getters, rootState, rootGetters) {
      const computed = {
        groupOrder: getters.groupOrder,
        orderItems: [],
        orderItemCount: 0,
        orderItemStagedCount: 0,
        subtotalIncludingStagedItems: 0
      };

      const orderDetails = rootGetters['session/orderDetails'];

      computed.subtotalIncludingStagedItems += state.order.subtotal;

      computed.orderItems = state.order.orderItems.map((item) => {
        computed.orderItemCount += item.quantity;

        return parseOrderItem(item);
      });

      state.order.orderItemsStaged.forEach((item) => {
        computed.orderItemStagedCount += item.quantity;
        computed.subtotalIncludingStagedItems += item.priceTotal;
      });

      if (orderDetails.fulfillmentType === 'delivery') {
        computed.delivery = {
          deliveryService: orderDetails.deliveryEstimate.deliveryService,
          fee: orderDetails.deliveryEstimate.fee
        };

        computed.serviceCharges = orderDetails.deliveryEstimate.deliveryServiceCharge;

        computed.total = state.order.total + orderDetails.deliveryEstimate.fee + orderDetails.deliveryEstimate.deliveryServiceCharge - state.order.serviceCharges;
      }

      else if (orderDetails.fulfillmentType === 'shipping') {
        const selectedShippingMethod = rootGetters['shipping/selectedShippingMethod'];

        computed.shipment = {
          fee: selectedShippingMethod.fee
        };

        computed.total = state.order.total + selectedShippingMethod.fee;
      }

      const order = { ...state.order, ...computed };

      return order;
    },

    orderErrors(state) {
      return state.orderErrors;
    },

    orders(state) {
      return state.orders;
    },

    orderItemCountForUser(state, getters, rootState, rootGetters) {
      const { order } = getters;
      const orderDetails = rootGetters['session/orderDetails'];

      if (orderDetails.orderType === 'group') {
        return order.groupOrder.you.orderItemCount;
      }

      if (orderDetails.orderType === 'table') {
        return order.orderItemStagedCount + order.orderItemCount;
      }

      return order.orderItemCount;
    },

    orderItemQuantitiesPerMenuItem(state) {
      const allOrderItems = state.order.orderItems.concat(state.order.orderItemsStaged);
      const quantities = {};

      allOrderItems.forEach((item) => {
        if (!quantities[item.menuItemId]) {
          quantities[item.menuItemId] = 0;
        }

        const existingQuantity = quantities[item.menuItemId] || 0;

        quantities[item.menuItemId] = existingQuantity + item.quantity;
      });

      return quantities;
    },

    orderItemsUnavailablePerSchedule(state) {
      const items = state.order.orderItems.filter(item => item.unavailablePerSchedule);

      return items;
    },

    orderTypeQueryParams(state, getters, rootState) {
      const { orderDetails } = rootState.session;
      const { menuType } = rootState.menu;
      const params = {};

      params['menu-type'] = menuType;

      if (state.menuDate) {
        params['menu-date'] = state.menuDate;
      }

      if (orderDetails.orderType === 'group') {
        params.groupMemberGuestId = orderDetails.groupMemberGuestId;
      }

      return params;
    },

    orderValidations(state, getters, rootState, rootGetters) {
      const selectedLocation = rootGetters['locations/selectedLocation'];
      const orderDetails = rootGetters['session/orderDetails'];
      const menu = rootGetters['menu/menu'];
      const { order } = getters;

      const validations = getLocationValidations(menu, orderDetails, selectedLocation);

      const formatOptions = {
        currencyCode: rootGetters['merchant/currencyCode'] || 'USD',
        locale: rootState.locale?.deviceLocale || 'en-US'
      };

      const result = {
        isValid: true,
        validations: validations || {},
        errors: {},
        warnings: {}
      };

      if (validations) {
        if (validations.orderMinimumAmount && validations.orderMinimumAmount > order.orderItemTotal) {
          result.errors.orderMinimumAmount = `This store requires a minimum purchase of ${formatCurrency(validations.orderMinimumAmount, formatOptions)}. Please adjust your order accordingly.`;
        }

        if (validations.orderMaximumAmount && validations.orderMaximumAmount < order.orderItemTotal) {
          result.errors.orderMaximumAmount = `This store has a maximum order size of ${formatCurrency(validations.orderMaximumAmount, formatOptions)}. Please adjust your order accordingly.`;
        }

        if (validations.orderMaximumItemCount && validations.orderMaximumItemCount < (order.orderItemCount + order.orderItemStagedCount)) {
          result.errors.orderMaximumItemCount = `Your cart has reached the total item limit of ${validations.orderMaximumItemCount} ${validations.orderMaximumItemCount > 1 ? 'items' : 'item'}`;
        }

        if (validations.orderMaximumItemCount && validations.orderMaximumItemCount <= (order.orderItemCount + order.orderItemStagedCount)) {
          result.warnings.orderMaximumItemCount = `Your cart has reached the total item limit of <b>${validations.orderMaximumItemCount}</b> ${validations.orderMaximumItemCount > 1 ? 'items' : 'item'}`;
        }

        result.isValid = Object.keys(result.errors).length === 0;
      }

      return result;
    },

    showCateringMenuButton(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];
      const selectedLocation = rootGetters['locations/selectedLocation'];
      const { fulfillmentType, orderType } = rootGetters['session/orderDetails'];
      const orderTypeIsCompatible = orderType !== 'group' && fulfillmentType !== 'dine-in';

      if (!merchant.features.catering || !orderTypeIsCompatible) {
        return false;
      }

      return selectedLocation.storeId ? selectedLocation.features.CateringOrdering : true;
    },

    showGroupOrderButton(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];
      const { order } = getters;
      const { fulfillmentType, orderType } = rootGetters['session/orderDetails'];
      const selectedLocation = rootGetters['locations/selectedLocation'];

      return !!selectedLocation.storeId
        && selectedLocation.isReadyForOrdering
        && merchant.options.signUpEnabled
        && !!merchant.features['group-order']
        && (order.groupOrder.you.isHost || orderType !== 'group')
        && orderType !== 'catering'
        && orderType !== 'table'
        && fulfillmentType !== 'dine-in';
    },

    tableNumber(state, getters, rootState) {
      const localStorageOrderDetails = storage.local.get('orderDetails');

      return state.order.tableNumber || rootState.session.route.tableNumber || localStorageOrderDetails.tableNumber;
    },

    tableNumberDisplay(state, getters) {
      return state.order.tableNumberDisplayName || getters.tableNumber;
    },

    upsellItems(state, getters, rootState, rootGetters) {
      const { upsellItems, upsellDisplayMode } = state.order;

      const { filteredCategories: filteredCategoryMenuItems } = rootGetters['menu/menu'];

      const parsedUpsellItems = parseUpsellItems(filteredCategoryMenuItems, upsellItems, upsellDisplayMode);

      return parsedUpsellItems;
    },

    userLocationType(state, getters, rootState) {
      const type = state.order.locationType || rootState.session.route.userLocationType;
      return parseUserLocationType(type);
    }
  },

  actions: {
    async addItem({ commit, dispatch, getters, rootGetters }, options) {
      const orderDetails = rootGetters['session/orderDetails'];

      const maxAllowed = options.item.maxAllowed || -1;
      const existingQuantity = getters.orderItemQuantitiesPerMenuItem[options.item.menuItemId] || 0;
      const itemQuantityIsValid = maxAllowed === -1 || maxAllowed >= existingQuantity + options.item.quantity;
      const { orderMaximumItemCount } = getters.orderValidations.validations;

      if (orderMaximumItemCount && orderMaximumItemCount <= getters.order.orderItemCount) {
        events.emit('notification', {
          type: 'error',
          message: `Unable to add item. Cart limit of ${orderMaximumItemCount} ${orderMaximumItemCount > 1 ? 'items' : 'item'} reached.`
        });
        return;
      }

      if (!itemQuantityIsValid) {
        events.emit('notification', {
          type: 'error',
          message: `Unable to add item. Max quantity allowed is ${maxAllowed}.`
        });
        return;
      }

      if (!options.silent) {
        commit('SET_ADD_ITEM_LOADING_STATUS', true);
      }

      if (orderDetails.orderType === 'table') {
        await dispatch('addItemStaged', options);
      }

      else {
        await dispatch('addItemStandard', options);
      }
    },

    async addItemStaged({ commit, dispatch, rootGetters }, options) {
      const { item } = options;

      const menu = rootGetters['menu/menu'];

      const orderItem = convertToOrderItem(item, menu);

      const orderItemsStaged = storage.local.get('orderItemsStaged') || [];

      orderItemsStaged.push(orderItem);

      await dispatch('updateStagedOrderItems', orderItemsStaged);

      commit('SET_ADD_ITEM_LOADING_STATUS', false);

      events.emit('notification', {
        type: 'success',
        message: 'Item was added to your cart.',
        duration: 3000
      });

      // Vue.triggerGoogleTrackingEvent('AddToOrder', 'Menu');

      bridge.triggerNewRelicEvent('ItemAddToOrder', 'Information', 'Item Added To Order');
    },

    async addItemStandard({ commit, dispatch, getters, state, rootGetters }, { item }) {
      try {
        const merchantLoyaltyOptions = rootGetters['merchant/loyaltyOptions'];

        const selectedLocation = rootGetters['locations/selectedLocation'];

        const { fulfillmentType } = rootGetters['session/orderDetails'];

        const mappedFulfillmentType = mapToFulfillmentType(fulfillmentType);

        const campaignId = rootGetters['session/campaignId'];

        let response;

        if (state.order.orderId) {
          response = await http.post(
            `/cardfree-api/v1/account/me/order/${state.order.orderId}/item`,
            campaignId ? { ...item, campaignId } : item,
            { params: getters.orderTypeQueryParams }
          );

          bridge.triggerNewRelicEvent('AddOrderItem', 'Information', 'New Item Added To Order', { item });
        }

        else {
          response = await http.post(
            '/cardfree-api/v1/account/me/order',
            {
              locationType: getters.userLocationType.type,
              orderItems: [item],
              fulfillmentType: mappedFulfillmentType,
              storeId: selectedLocation.storeId,
              ...(campaignId && { campaignId })
            },
            { params: getters.orderTypeQueryParams }
          );

          bridge.triggerNewRelicEvent('StartOrder', 'Information', 'New Order Started', { orderItems: [item] });

          if (merchantLoyaltyOptions.isStoreSpecificLoyalty) {
            await dispatch('offers/getLoyaltySummary', { storeId: selectedLocation.storeId }, { root: true });
          }
        }

        const eventData = { itemInfo: [item] };

        bridge.googleEventTracking.postEvent('add_to_cart', eventData);

        await dispatch('updateOrderWithResponse', {
          order: response.data,
          validate: true
        });

        events.emit('notification', {
          type: 'success',
          message: 'Item was added to your cart.'
        });
      }

      catch (error) {
        const capiError = parseCapiError(error);

        errorHandlers.generic(error);

        if (capiError.codes.includes('order-item-unavailable')) {
          dispatch('menu/getMenu', {
            forceLoad: true,
            silent: true
          }, { root: true });
        }

        else if (capiError.codes.includes('order-locked')) {
          dispatch('getOrder', { silent: true });
        }
      }

      finally {
        commit('SET_ADD_ITEM_LOADING_STATUS', false);
      }
    },

    async assignOrderToUser({ commit, dispatch, getters, rootGetters }) {
      const currentUser = rootGetters['session/currentUser'];
      const { order } = getters;
      const orderDetails = rootGetters['session/orderDetails'];

      if (!currentUser.accountId || !order.orderId || !order.orderAttributes.canAssignAccount || order.orderState !== 'Open' || order.isAssigning) {
        return;
      }

      commit('SET_ORDER_ASSIGNING_STATUS', true);

      if (['pay', 'table'].includes(orderDetails.orderType)) {
        await dispatch('assignOrderToUserPayAtTable');
      }

      else {
        await dispatch('assignOrderToUserOrderAhead');
      }

      commit('SET_ORDER_ASSIGNING_STATUS', false);
    },

    async assignOrderToUserOrderAhead({ getters, rootGetters }) {
      try {
        const { order } = getters;
        const orderDetails = rootGetters['session/orderDetails'];

        if (orderDetails.orderType === 'group') {
          await http.put(`/cardfree-api/v1/account/me/order/group/${order.groupOrder.token}/member/${order.groupOrder.you.guestId}/register`);
        }

        else {
          await http.put(`/cardfree-api/v1/account/me/order/${order.orderId}/assign`);
        }
      }

      catch (error) {
        errorHandlers.ordering(error);
      }
    },

    async assignOrderToUserPayAtTable({ dispatch, getters, rootGetters }) {
      try {
        const { order } = getters;
        const isPrimaryOrderRoomUser = rootGetters['socket/isPrimaryOrderRoomUser'];

        if (isPrimaryOrderRoomUser) {
          await http.put(`/cardfree-api/v1/account/me/order/${order.orderId}/assign`);
          await dispatch('getOrderAtTable', { silent: true });
        }
      }

      catch (error) {
        errorHandlers.silent(error);
      }
    },

    cacheOrderSelection({ commit }, order) {
      commit('SET_ORDER', order);

      commit('SET_CACHED_ORDER_ID_SELECTION', order.orderId);
    },

    async cancelAllAuthorizedPayments({ dispatch, state }, options) {
      try {
        await http.delete(`/cardfree-api/v1/account/me/order/${state.order.orderId}/payment`);

        dispatch('socket/cancelPayments', { reason: options.reason }, { root: true });
      }

      catch (error) {
        throw error;
      }
    },

    async cancelOrder({ dispatch, getters }) {
      try {
        await http.delete(`/cardfree-api/v1/account/me/order/${getters.order.orderId}`);
      }

      catch (error) {
        errorHandlers.silent(error);
      }

      finally {
        dispatch('session/unsetOrder', { cancelled: true }, { root: true });

        events.emit('notification', {
          type: 'success',
          message: 'Your order has been reset.'
        });
      }
    },

    editItem({ dispatch }, { item, ...options }) {
      return dispatch('editItems', {
        items: [item],
        ...options
      });
    },

    async editItems({ commit, dispatch, rootGetters }, options) {
      const { orderType } = rootGetters['session/orderDetails'];

      const actionName = orderType === 'table' ? 'editItemsStaged' : 'editItemsStandard';

      if (!options.silent) {
        commit('SET_ADD_ITEM_LOADING_STATUS', true);
      }

      await dispatch(actionName, options);
    },

    async editItemsStaged({ commit, dispatch, rootGetters }, { items, ...options }) {
      const menu = rootGetters['menu/menu'];

      const orderItemsStaged = storage.local.get('orderItemsStaged') || [];

      items.forEach((item) => {
        const orderItem = convertToOrderItem(item, menu, item.orderItemId);

        const orderItemToUpdateIndex = orderItemsStaged.findIndex(stagedItem => stagedItem.orderItemId === orderItem.orderItemId);

        if (orderItemToUpdateIndex > -1) {
          orderItemsStaged[orderItemToUpdateIndex] = orderItem;
        }

        else {
          orderItemsStaged.push(orderItem);
        }
      });

      await dispatch('updateStagedOrderItems', orderItemsStaged);

      if (!options.silent) {
        commit('SET_ADD_ITEM_LOADING_STATUS', false);

        events.emit('notification', {
          type: 'success',
          message: `${items.length > 1 ? 'Items were' : 'Item was'} updated in your cart.`
        });
      }
    },

    async editItemsStandard(
      { commit, dispatch, getters, state },
      { items, ...options }
    ) {
      try {
        const editItemPromises = items.map(item => http.put(`/cardfree-api/v1/account/me/order/${state.order.orderId}/item/${item.orderItemId}`, {
          favoriteItemId: item.favoriteItemId,
          menuItemId: item.menuItemId,
          orderItemId: item.orderItemId,
          specialInstructions: item.specialInstructions || undefined,
          quantity: item.quantity,
          modifiers: item.modifiers
        }, {
          params: getters.orderTypeQueryParams
        }));

        const responses = await Promise.all(editItemPromises);

        await dispatch('updateOrderWithResponse', {
          order: responses[responses.length - 1].data,
          validate: 'validate' in options ? options.validate : true
        });

        if (!options.silent) {
          events.emit('notification', {
            type: 'success',
            message: `${items.length > 1 ? 'Items were' : 'Item was'} updated in your cart.`
          });
        }
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        if (!options.silent) {
          commit('SET_ADD_ITEM_LOADING_STATUS', false);
        }
      }
    },

    async getAllOrdersAtTable({ dispatch, getters, rootState, rootGetters }, options = {}) {
      try {
        let response;

        const query = options.query || rootState.session.route;
        const pi = query.partnerIdentification || undefined;

        if (query.sublocationToken) {
          const args = [`/cardfree-api/v1/pat/pay/${query.sublocationToken}`];
          if (query.orderId) args.push({ params: { oi: query.orderId } });

          response = await http.get(...args);
        }

        else if (query.ticketNumber && query.merchantLocationId) {
          response = await http.get(`/cardfree-api/v1/pat/pay/${query.merchantLocationId}/tickets`, {
            params: {
              pi,
              tn: query.ticketNumber
            }
          });
        }

        else if (query.ticketNumber && query.posLocationId) {
          response = await http.get(`/cardfree-api/v1/pat/pay/${query.posLocationId}/tickets`, {
            params: {
              pi,
              tn: query.ticketNumber,
              isPl: true
            }
          });
        }

        else if (query.tableNumber && query.merchantLocationId) {
          if (query.orderType === 'pat') {
            response = await http.get(`/cardfree-api/v1/pat/pay/${query.merchantLocationId}/tickets`, {
              params: {
                pi,
                t: query.tableNumber
              }
            });
          }
          else {
            /* creates a new Order @ Table order */
            response = await http.post(
              `/cardfree-api/v1/oat/find/${query.merchantLocationId}`,
              null,
              {
                params: {
                  beginOrder: true,
                  locationType: getters.userLocationType.type,
                  tableNumber: query.tableNumber,

                  /* add `isOpenTab` query param when location has it enabled */
                  isOpenTab: rootGetters['locations/selectedLocation']?.features.OpenTab ? true : undefined
                }
              }
            );
          }
        }

        else if (query.customQuery && query.merchantLocationId) {
          const parsedQueryParams = {};
          const segments = query.customQuery.split(',');

          segments.forEach((segment) => {
            const key = segment.split(':')[0];
            const value = segment.split(':')[1];
            parsedQueryParams[key] = value;
          });

          response = await http.get(`/cardfree-api/v1/pat/pay/${query.merchantLocationId}/tickets`, {
            params: parsedQueryParams
          });
        }

        else {
          return;
        }

        if (response.data.errors) {
          /**
          * NOTE: When CAPI returns an `errors[]` array, the whole response object gets
          * shuffled around with an extra, nesting data param (`response.data.data`). This
          * hack will reformat the response in to the object we expect (all data living
          * directly on `response.data`).
          */

          response.data = {
            ...response.data.data,
            errors: response.data.errors
          };
        }
        await dispatch('getAllOrdersAtTableResponseHandler', {
          dontResetIntervalSync: options.dontResetIntervalSync,
          paymentConflict: options.paymentConflict,
          silent: options.silent,
          response
        });
      }

      catch (error) {
        throw error;
      }
    },

    async getAllOrdersAtTableResponseHandler({ commit, dispatch }, options) {
      try {
        const { response } = options;

        dispatch('socket/syncOrdersAtTable', {
          data: response.data,
          paymentConflict: options.paymentConflict || false
        }, { root: true });

        const { orders } = response.data;

        commit('SET_ORDER_ERRORS', response.data.errors);

        commit('SET_ORDERS', orders);

        if (orders.length > 0) {
          await dispatch('locations/selectLocation', {
            location: orders[0].store,
            withMenu: true,
            silent: options.silent
          }, { root: true });

          if (!options.dontResetIntervalSync) {
            dispatch('syncOrdersAtTableIntervalStart');
          }

          await dispatch('loadStagedOrderItems');
        }

        else {
          const error = new Error('no open orders');

          throw error;
        }
      }

      catch (error) {
        throw error;
      }
    },

    async getOrder({ commit, dispatch, getters, rootState }, options = {}) {
      const { session } = rootState;

      const query = options.query || session.route;

      try {
        let response;

        if (query.orderGuid) {
          if (!options.silent) {
            commit('SET_ORDER_LOADING_STATUS', true);
          }

          response = await http.get(`/cardfree-api/v1/account/me/order/${query.orderGuid}`, {
            params: {
              checkUnavailableItems: options.checkUnavailableItems,
              includePayments: true,
              ...getters.orderTypeQueryParams
            }
          });

          await dispatch('updateOrderWithResponse', {
            order: response.data,
            validate: options.validate,
            validateDayparts: options.validateDayparts
          });
        }

        if (session.orderId) {
          if (!options.silent) {
            commit('SET_ORDER_LOADING_STATUS', true);
          }

          if (options.pickupTime) {
            commit('SET_MENU_DATE', options.pickupTime);
          }

          response = await http.get(`/cardfree-api/v1/account/me/order/${session.orderId}`, {
            params: {
              includePayments: true,
              checkUnavailableItems: options.checkUnavailableItems,
              ...getters.orderTypeQueryParams
            }
          });


          if (session.locationId && session.locationId !== response.data.store.storeId) {
            throw new Error('Order location does not match selected location.');
          }

          await dispatch('updateOrderWithResponse', {
            order: response.data,
            validate: options.validate,
            validateDayparts: options.validateDayparts
          });
        }
      }

      catch (error) {
        errorHandlers.ordering(error);
      }

      finally {
        commit('SET_ORDER_LOADING_STATUS', false);
      }
    },

    // this is getting the pay at table order
    async getOrderAtTable({ commit, dispatch, rootState, state }, options = {}) {
      try {
        const { orderId, tableNumber } = rootState.session.route;

        if (state.cachedOrderIdSelection && Number(orderId) === Number(state.cachedOrderIdSelection)) {
          commit('SET_CACHED_ORDER_ID_SELECTION', null);

          return;
        }

        if (!options.silent) {
          commit('SET_ORDER_LOADING_STATUS', true);
        }

        await dispatch('getAllOrdersAtTable', options);

        const orderByOrderId = state.orders.find(o => Number(o.orderId) === Number(orderId));
        const ordersByTableNumber = state.orders.filter(o => Number(o.tableNumber) === Number(tableNumber));

        if (orderByOrderId || ordersByTableNumber.length === 1) {
          const order = orderByOrderId || ordersByTableNumber[0];

          /**
           * Check if the order total has changed due to changes made directly on the POS.
           * If so, kick the user back to the checkout view to re-review the order before
           * entering the payment user flow again.
           */
          if (options.confirmTotalBeforePayment && state.order.total !== order.total) {
            dispatch(
              'modals/openModal',
              {
                isLocked: true,
                name: 'totalChangedWarning',
                closeModal: 'paymentInformation'
              },
              { root: true }
            );
          }

          commit('SET_ORDER', order);

          commit('SET_ORDER_LOADING_STATUS', false);

          dispatch('updateOrder', {
            orderType: order.orderType === 'OrderAndPay' ? 'table' : 'pay'
          });
        }

        else if (ordersByTableNumber.length > 1) {
          const error = new Error(`There were multiple orders found at table ${tableNumber}`);
          error.code = 'MULTIPLE_ORDERS_FOUND';

          throw error;
        }

        else {
          const error = new Error('There was no order found');
          error.code = 'ORDER_NOT_FOUND';

          throw error;
        }
      }

      catch (error) {
        commit('SET_ORDER_LOADING_STATUS', false);

        throw error;
      }
    },

    async getUpsellItems({ commit, rootGetters, state }) {
      try {
        const selectedLocation = rootGetters['locations/selectedLocation'];

        const orderDetails = rootGetters['session/orderDetails'];

        const menuItemIds = [];

        state.order.orderItems.forEach((item) => {
          menuItemIds.push(item.menuItemId);
        });

        if (orderDetails.orderType === 'table') {
          state.order.orderItemsStaged.forEach((item) => {
            menuItemIds.push(item.menuItemId);
          });
        }

        const response = await http.post('/cardfree-api/v1/upsell', {
          storeId: selectedLocation.storeId,
          menuItemIds
        });

        commit('SET_UPSELL_ITEMS', response.data);
      }

      catch (error) {
        errorHandlers.silent(error);

        commit('SET_UPSELL_ITEMS', []);
      }
    },

    async groupOrderCreate({ dispatch, getters, rootGetters }, options) {
      try {
        const { primarySmsNumber } = rootGetters['session/currentUser'];
        const { storeId } = rootGetters['locations/selectedLocation'];
        const { orderId } = rootGetters['orders/order'];
        const campaignId = rootGetters['session/campaignId'];

        const response = await http.post('/cardfree-api/v1/account/me/order/group', {
          groupMemberCountLimit: 0,
          groupMemberSubtotalLimit: 0,
          groupName: `${options.hostName}'s Group Order`,
          hostName: options.hostName,
          orderId,
          phoneNumber: primarySmsNumber,
          storeId,
          subtotalLimit: 0,
          ...(campaignId && { campaignId })
        }, {
          params: getters.orderTypeQueryParams
        });

        await dispatch('updateOrder', {
          groupMemberGuestId: response.data.groupOrder.members[0].guestId,
          orderType: 'group'
        });

        await dispatch('updateOrderWithResponse', {
          order: response.data
        });
      }

      catch (error) {
        throw error;
      }
    },

    async groupOrderJoin({ dispatch }, options) {
      try {
        const response = await http.post(`/cardfree-api/v1/account/me/order/group/${options.groupToken}/member`, {
          displayName: options.displayName,
          phoneNumber: options.phoneNumber
        });

        const member = response.data.groupOrder.members.find(m => !!m.guestId);

        if (!member) {
          throw new Error('Could not find newly added user.');
        }

        else {
          await dispatch('updateOrder', {
            fulfillmentType: 'inherit',
            groupMemberGuestId: member.guestId,
            orderType: 'group'
          });

          await dispatch('updateOrderWithResponse', {
            justJoinedGroup: true,
            order: response.data
          });
        }
      }

      catch (error) {
        throw error;
      }
    },

    async groupOrderLeave({ dispatch, getters }) {
      let didLeave = true;

      try {
        const { groupOrder } = getters.order;

        await http.delete(`/cardfree-api/v1/account/me/order/group/${groupOrder.token}/member/${groupOrder.you.guestId}`);
      }

      catch (error) {
        const capiError = parseCapiError(error);

        if (capiError.codes.includes('order-locked')) {
          didLeave = false;

          events.emit('notification', {
            type: 'error',
            clickToDismiss: true,
            message: 'Oops! You can\'t leave right now. Your host has locked the group order.'
          });
        }

        else {
          errorHandlers.silent(error);
        }
      }

      finally {
        if (didLeave) {
          dispatch('session/unsetOrder', {}, { root: true });

          events.emit('notification', {
            type: 'success',
            message: 'You have left the group order.'
          });
        }
      }
    },

    async groupOrderRemoveMember({ dispatch, getters }, options) {
      try {
        const { groupOrder } = getters.order;
        await http.delete(`/cardfree-api/v1/account/me/order/group/${groupOrder.token}/member/${options.member.guestId}`);
        await dispatch('getOrder', { silent: true });

        events.emit('notification', {
          type: 'success',
          message: `${options.member.displayName} was removed from your group.`
        });
      }

      catch (error) {
        throw error;
      }
    },

    async groupOrderUpdateLock({ dispatch, getters }, options) {
      try {
        const { groupOrder } = getters.order;

        const response = await http.put(`/cardfree-api/v1/account/me/order/group/${groupOrder.token}/lock`, {
          isLocked: options.isLocked
        });

        // if (options.isLocked) {
        // Vue.triggerGoogleTrackingEvent('LockOrder', 'GroupOrder');
        // }

        await dispatch('updateOrderWithResponse', { order: response.data });
      }

      catch (error) {
        // Vue.$triggerGoogleTrackingEvent('LockOrder', 'GroupOrder');
        throw error;
      }
    },

    async handleSubmitError({ dispatch }, data) {
      const { error, pickupTime } = data;
      const capiError = parseCapiError(error);

      /* NOTE: There's no need to block execution and await for this action */
      dispatch('offers/getOffers', {
        silent: true,
        silentError: true
      }, { root: true });

      await dispatch('getOrder', {
        checkUnavailableItems: true,
        pickupTime,
        silent: true,
        validate: true,
        validateDayparts: true
      });

      if (
        capiError.codes.includes('order-items-unavailable')
        || capiError.codes.includes('order-items-unavailable-at-pickup')
        || capiError.codes.includes('order-items-out-of-stock')
      ) {
        /* NOTE: There's no need to block execution and await for this action */
        dispatch('menu/getMenu', {
          forceLoad: true,
          silent: true
        }, { root: true });
      }

      else {
        errorHandlers.generic(error, {
          fallbackMessage: 'Failed to submit order. Please verify payment details.'
        });
      }
    },

    async handleUnavailableItems(
      { dispatch, getters, rootGetters },
      { unavailableItems = [], invalidDaypartItems = [] }
    ) {
      try {
        const { orderType } = rootGetters['session/orderDetails'];
        const { groupOrder } = getters.order;
        const unavailableItemIds = unavailableItems.map(item => item.orderItemId);

        dispatch('modals/openModal', {
          name: 'unavailableItems',
          data: { invalidDaypartItems, unavailableItems }
        }, { root: true });

        if (orderType === 'group') {
          await http.put(`/cardfree-api/v1/account/me/order/group/${groupOrder.token}/lock`, {
            isLocked: false
          });
        }

        if (unavailableItemIds.length) {
          await dispatch('removeItems', {
            orderItemIds: unavailableItemIds,
            orderItems: unavailableItems,
            silent: true,
            validate: false /* don't revalidate because it will cause this method to run again
            with the unavailable items already removed if an invalid daypart item still remains */
          });
        }
      }

      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async loadStagedOrderItems({ dispatch }) {
      const orderItemsStaged = storage.local.get('orderItemsStaged') || [];

      await dispatch('updateStagedOrderItems', orderItemsStaged);
    },

    async normalizeOrderDetails({ dispatch, rootGetters, rootState }, options) {
      const orderDetails = rootGetters['session/orderDetails'];
      const { isPortalAuthSession } = rootState.session;

      // Portal auth (house account) sessions are locked to the catering flow. When this flow
      // resets the order, preserve catering as the orderType rather than falling back to the
      // caller's default (typically 'standard').
      const lockToCatering = isPortalAuthSession && options.orderTypes.includes('catering');

      let shouldReset = false;

      if (!options.orderTypes.includes(orderDetails.orderType) || !orderDetails.fulfillmentType) {
        shouldReset = true;
      }

      if (shouldReset) {
        await dispatch('session/unsetOrder', {
          doNotResetRouteParams: true
        }, { root: true });

        await dispatch('session/updateOrderDetails', {
          fulfillmentType: options.defaultFulfillmentType,
          menuType: null,
          orderType: lockToCatering ? 'catering' : options.defaultOrderType
        }, { root: true });
      }
    },

    async redeemOffer({ dispatch, rootGetters, state }, options) {
      try {
        const orderDetails = rootGetters['session/orderDetails'];

        /**
         * NOTE:
         * In the future we might pass true for this param:
         *
         * ?returnAllOffers=true
         *
         * This would cause the endpoint to always return a 200 with an updated offers array.
         * An offer might be currently invalid, and the status would reflect that.
        */

        const response = await http.post(`/cardfree-api/v1/account/me/order/${state.order.orderId}/offer?returnAllOffers=false`, [
          {
            loyaltyConversionThresholdPublicId: options.loyaltyConversionThresholdPublicId,
            offerAllocationPublicId: options.offerAllocationPublicId,
            promotionCode: options.promotionCode
          }
        ]);

        if (['pay', 'table'].includes(orderDetails.orderType)) {
          await dispatch('getOrderAtTable', { silent: true });
        }

        else {
          await dispatch('updateOrderWithResponse', {
            order: response.data
          });
        }
      }

      catch (error) {
        await dispatch('getOrder', { silent: true });

        throw error;
      }
    },

    async removeOffer({ commit, dispatch, rootGetters, state }, { id }) {
      try {
        const orderDetails = rootGetters['session/orderDetails'];

        commit('SET_REMOVE_OFFER_LOADING_STATUS', {
          id,
          isStarting: true
        });

        await http.delete(`/cardfree-api/v1/account/me/order/${state.order.orderId}/offer/${id}`);

        if (['pay', 'table'].includes(orderDetails.orderType)) {
          await dispatch('getOrderAtTable', { silent: true });
        }

        else {
          /**
           * Re-fetch the order rather than relying on the DELETE response,
           * which may not include recalculated totals after offer removal.
           */
          await dispatch('getOrder', { silent: true });
        }
      }

      catch (error) {
        throw error;
      }

      finally {
        commit('SET_REMOVE_OFFER_LOADING_STATUS', { id });
      }
    },

    removeItem({ dispatch }, { orderItemId, ...options }) {
      return dispatch('removeItems', {
        orderItemIds: [orderItemId],
        ...options
      });
    },

    removeItems({ dispatch, rootGetters }, options) {
      const { orderType } = rootGetters['session/orderDetails'];
      const actionName = orderType === 'table' ? 'removeItemsStaged' : 'removeItemsStandard';

      return dispatch(actionName, options);
    },

    async removeItemsStaged({ dispatch }, { orderItemIds, ...options }) {
      let orderItemsStaged = storage.local.get('orderItemsStaged') || [];

      orderItemsStaged = orderItemsStaged.filter(item => !orderItemIds.includes(item.orderItemId));

      await dispatch('updateStagedOrderItems', orderItemsStaged);

      if (!options.silent) {
        events.emit('notification', {
          type: 'success',
          message: `${orderItemIds.length > 1 ? 'Items were' : 'Item was'} removed from your cart.`
        });
      }
    },

    async removeItemsStandard(
      { commit, dispatch, getters, state },
      { orderItemIds, ...options }
    ) {
      try {
        if (!options.silent) {
          commit('SET_REMOVE_ITEM_LOADING_STATUS', {
            isStarting: true,
            ids: orderItemIds
          });
        }

        const removeItemPromises = orderItemIds.map(orderItemId => (
          http.delete(
            `/cardfree-api/v1/account/me/order/${state.order.orderId}/item/${orderItemId}`,
            { params: getters.orderTypeQueryParams }
          )
        ));

        const responses = await Promise.all(removeItemPromises);

        const eventData = { itemInfo: options.orderItems };

        bridge.googleEventTracking.postEvent('remove_from_cart', eventData);

        await dispatch('updateOrderWithResponse', {
          order: responses[responses.length - 1].data,
          validate: 'validate' in options ? options.validate : true
        });

        if (!options.silent) {
          events.emit('notification', {
            type: 'success',
            message: `${orderItemIds.length > 1 ? 'Items were' : 'Item was'} removed from your cart.`
          });
        }
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        if (!options.silent) {
          commit('SET_REMOVE_ITEM_LOADING_STATUS', {
            ids: orderItemIds
          });
        }
      }
    },

    async reorder({ dispatch, getters, rootGetters, state }, order) {
      try {
        const unavailableItems = getUnavailableItemsAndModifiers(order);

        if (unavailableItems.length) {
          dispatch('modals/openModal', {
            name: 'reorder',
            data: { order, unavailableItems }
          }, { root: true });

          return;
        }

        let orderResponse;
        const favoriteItems = rootGetters['favoriteItems/favoriteItems'];
        const selectedLocation = rootGetters['locations/selectedLocation'];
        const campaignId = rootGetters['session/campaignId'];

        const orderItems = order.orderItems.map((item) => {
          const itemToAdd = {
            menuItemId: item.menuItemId,
            specialInstructions: item.specialInstructions,
            quantity: item.quantity,
            modifiers: []
          };

          const modifiers = item.modifiers.map((modifier) => {
            let nestedModifiers = [];

            if (modifier.modifiers) {
              nestedModifiers = modifier.modifiers.map(nestedModifier => ({
                menuItemId: nestedModifier.menuItemId,
                quantity: nestedModifier.quantity
              }));
            }

            return {
              menuItemId: modifier.menuItemId,
              modifiers: nestedModifiers,
              quantity: modifier.quantity
            };
          });

          itemToAdd.modifiers = modifiers;

          if (item.favoriteItemId) {
            const favorite = favoriteItems.items.find(favoriteItem => favoriteItem.favoriteItemId === item.favoriteItemId);

            if (favorite) {
              itemToAdd.favoriteItemId = item.favoriteItemId;
            }
          }

          return itemToAdd;
        });

        if (state.order.orderId) {
          const requestItems = campaignId
            ? orderItems.map(item => ({ ...item, campaignId }))
            : orderItems;

          orderResponse = await http.post(`/cardfree-api/v1/account/me/order/${state.order.orderId}/items`, requestItems, {
            params: getters.orderTypeQueryParams
          });
        }

        else {
          if (!selectedLocation.storeId) {
            const locationResponse = await http.get('/cardfree-api/v1/location/store', {
              params: {
                ids: order.store.storeId
              }
            });
            const location = locationResponse.data.data[0];

            await dispatch('locations/selectLocation', {
              location,
              silent: true,
              withMenu: true
            }, { root: true });
          }

          orderResponse = await http.post('/cardfree-api/v1/account/me/order', {
            locationType: getters.userLocationType.type,
            orderItems,
            storeId: order.store.storeId,
            ...(campaignId && { campaignId })
          }, {
            params: getters.orderTypeQueryParams
          });
        }

        await dispatch('updateOrderWithResponse', {
          order: orderResponse.data,
          validate: true
        });

        events.emit('notification', {
          type: 'success',
          message: 'Items were added to your cart.'
        });
      }

      catch (error) {
        throw error;
      }
    },

    // Deprecated - do not use going forward, see addStagedItemsToOrder in orderAtTable store
    async sendStagedItemsToKitchen({ commit, dispatch, state, rootGetters }) {
      try {
        let { orderId } = state.order;

        if (!orderId) {
          await dispatch('getAllOrdersAtTable');

          orderId = state.orders[0].orderId;

          commit('SET_ORDER', state.orders[0]);
        }

        const campaignId = rootGetters['session/campaignId'];

        const items = state.order.orderItemsStaged.map((item) => {
          const modifiers = item.modifiers.map(modifier => ({
            menuItemId: modifier.menuItemId,
            quantity: modifier.quantity
          }));

          return {
            isUpsell: item.isUpsell,
            menuItemId: item.menuItemId,
            categoryId: item.categoryId,
            modifiers,
            quantity: item.quantity,
            specialInstructions: item.specialInstructions,
            ...(campaignId && { campaignId })
          };
        });

        const response = await http.post(`/cardfree-api/v1/oat/order/${orderId}/items`, items);

        const order = response.data.orders.find(o => o.orderId === orderId);

        await dispatch('cacheOrderSelection', order);

        await dispatch('socket/syncOrdersAtTable', {
          data: response.data
        }, { root: true });

        await dispatch('updateStagedOrderItems', []);

        events.emit('notification', {
          type: 'success',
          message: 'Your order was submitted to the kitchen.'
        });
      }

      catch (error) {
        throw error;
      }
    },

    async startTipOnlyOrder({ getters, rootGetters }) {
      const { storeId } = rootGetters['locations/selectedLocation'];
      const orderType = getters.currentCapiOrderType;

      try {
        const { data } = await http.post(
          '/cardfree-api/v1/account/me/order',
          { storeId, orderType }
        );

        return data;
      }
      catch (error) {
        throw error;
      }
    },

    async submitOrder({ dispatch, commit, rootGetters, state }, options) {
      try {
        const menu = rootGetters['menu/menu'];
        const session = rootGetters['session/session'];
        const selectedLocation = rootGetters['locations/selectedLocation'];
        const campaignId = rootGetters['session/campaignId'];
        const { tdsData, orderItems, offers, cloverBrowserInfo } = state.order;

        const body = await normalizePaymentRequestBody({
          contactInfo: options.contactInfo,
          paymentGateway: selectedLocation.paymentGateway,
          paymentMethod: options.paymentMethod,
          ...tdsData && { tdsData },
          ...cloverBrowserInfo && { cloverBrowserInfo }
        }, {
          deliveryRequest: options.deliveryRequest,
          orderLevelInstructions: options.orderLevelInstructions,
          pickupDate: options.pickupDate,
          pickupLocation: options.pickupLocation,
          shipmentRequest: options.shipmentRequest,
          sublocationId: options.sublocationId,
          tip: options.tip,
          includeUtensils: options.includeUtensils
        });

        if (campaignId) {
          body.campaignId = campaignId;
        }

        /** Clear so the browser info can't leak onto a later non-3DS retry. */
        if (cloverBrowserInfo) commit('SET_CLOVER_BROWSER_INFO');

        if (window._debugPay) {
          window._paymentRequestBody = body;
          return;
        }

        const [firstOffer] = offers;

        const eventData = {
          itemInfo: orderItems,
          userInfo: options.contactInfo,
          paymentInfo: {
            paymentType: body.paymentType,
            tip: options.tip,
            tax: state.order.tax,
            total: state.order.orderItemTotal,
            ...firstOffer && { promoCode: firstOffer.cardfreeOfferPublicId },
            ...state.order.discount && { discount: state.order.discount }
          }
        };

        bridge.googleEventTracking.postEvent('add_payment_info', eventData);

        const submitResponse = await http.post(`/cardfree-api/v1/account/me/order/${state.order.orderId}/submit`, body, {
          params: {
            'menu-type': menu.menuType,
            sendReceipt: true,
            sendSmsReceipt: session.smsReceiptOptIn
          }
        });

        let responseData = submitResponse.data;

        /** Only Clover 3DS payments have paymentActions to resolve. */
        if (cloverBrowserInfo) {
          /**
           * The /submit Confirm re-validates the whole order (unlike the pay-at-table /payment Confirm), so it
           * must echo the original submission's metadata. Drop only the card details, tip, and browser info —
           * the initial charge already captured those. resolveCloverPaymentActions adds paymentAction +
           * threeds.status on top.
           */
          const {
            cardNumber, cardNumberLastFour, cardType, cvv, expiration, billingAddress,
            encryptedCardNumber, encryptedCvv, token, tip, threeds,
            ...confirmMetadata
          } = body;

          /**
           * Clover 3DS post-payment action loop: the /submit response may report actionRequired with a Clover3ds
           * paymentAction (method fingerprinting, then challenge). Resolve them before the order is finalized.
           * Confirms resubmit to /submit — the payment was created there, and /payment can't confirm it.
           */
          const payments = submitResponse.data?.payments || [];
          const yourPayment = payments[payments.length - 1];

          responseData = await resolveCloverPaymentActions({
            data: submitResponse.data,
            yourPayment,
            postConfirm: async (confirmBody) => {
              const submitBody = { ...confirmMetadata, ...confirmBody };

              const { data } = await http.post(`/cardfree-api/v1/account/me/order/${state.order.orderId}/submit`, submitBody, {
                params: {
                  'menu-type': menu.menuType,
                  sendReceipt: true,
                  sendSmsReceipt: session.smsReceiptOptIn
                }
              });

              return data;
            }
          });
        }

        dispatch('session/clearCampaignId', null, { root: true });

        const { ticketNumber, externalOrderId } = responseData;

        const purchaseEventData = {
          ...eventData,
          orderInfo: {
            ticketNumber,
            externalOrderId
          }
        };

        bridge.googleEventTracking.postEvent('purchase', purchaseEventData);

        bridge.triggerNewRelicEvent('SubmitOrder', 'Information', 'Order Submission');

        const payment = responseData.payments[responseData.payments.length - 1];

        dispatch('session/setPrefillAccountInfo', {
          email: options.contactInfo.email,
          fullName: options.contactInfo.fullName,
          primarySmsNumber: options.contactInfo.phoneNumber
        }, { root: true });

        await dispatch('updateOrderWithResponse', {
          order: responseData
        });

        /**
         * I'm not sure why we're waiting half a second to unset the order.
         * The previous developer had put this in place, without any explanation.
         */
        setTimeout(() => {
          dispatch(
            'session/unsetOrder',
            /**
             * Temp hack to potentially fix an unreproducable bug.
             * https://cardfree.atlassian.net/browse/WAP-1481
             */
            { skipMenuTypeChange: true },
            { root: true }
          );
        }, 500);

        return payment;
      }

      catch (error) {
        throw error;
      }

      finally {
        commit('SET_TDS_DATA');
      }
    },

    syncGroupOrderConditionally({ dispatch, rootState }) {
      const { session } = rootState;

      if (session.orderId && session.orderDetails.orderType === 'group') {
        dispatch('getOrder', { silent: true });
      }

      else {
        dispatch('syncGroupOrderIntervalStop');
      }
    },

    syncGroupOrderIntervalStart({ dispatch }) {
      dispatch('syncGroupOrderIntervalStop');

      window.syncGroupOrderInterval = setInterval(() => {
        dispatch('syncGroupOrderConditionally');
      }, 30000);
    },

    syncGroupOrderIntervalStop() {
      clearInterval(window.syncGroupOrderInterval);
    },

    async syncOrdersAtTableConditionally({ dispatch, rootGetters }) {
      try {
        const orderRoom = rootGetters['socket/orderRoom'];

        if (!orderRoom.lostConnection) {
          await dispatch('getAllOrdersAtTable', {
            dontResetIntervalSync: true,
            silent: true
          });
        }
      }

      catch (error) {
        logger.error(error);
      }
    },

    syncOrdersAtTableIntervalStart({ dispatch }) {
      dispatch('syncOrdersAtTableIntervalStop');

      window.syncOrdersAtTableInterval = setInterval(() => {
        dispatch('syncOrdersAtTableConditionally');
      }, 30000);
    },

    syncOrdersAtTableIntervalStop() {
      clearInterval(window.syncOrdersAtTableInterval);
    },

    async updateLocationWithGroupOrderResponse({ dispatch, rootGetters }, options) {
      try {
        const { justJoinedGroup, order } = options;
        const location = rootGetters['locations/selectedLocation'];

        if (order.store.storeId !== location.storeId) {
          dispatch('session/updateLocationId', order.store.storeId, { root: true });

          await dispatch('locations/getLocation', {
            silent: true,
            withMenu: true
          }, { root: true });

          if (!justJoinedGroup) {
            events.emit('notification', {
              type: 'info',
              message: 'The host updated your location. Items in your cart may have changed.',
              clickToDismiss: true
            });
          }
        }
      }

      catch (error) {
        throw error;
      }
    },

    updateLockedStatusWithGroupOrderResponse(context, options) {
      const { order, previousOrder } = options;
      const lockStatusChanged = previousOrder.groupOrder.isLocked !== order.groupOrder.isLocked;

      if (lockStatusChanged && !order.groupOrder.you.isHost) {
        if (order.groupOrder.isLocked) {
          events.emit('notification', {
            type: 'info',
            message: 'Your cart has been locked by your host.'
          });
        }

        else {
          events.emit('notification', {
            type: 'success',
            message: 'Your cart has been unlocked.'
          });
        }
      }
    },

    async updateOrder({ dispatch, rootGetters }, options) {
      try {
        const orderDetails = rootGetters['session/orderDetails'];

        const updatedOrderDetails = {
          ...orderDetails,
          deliveryAddress: 'deliveryAddress' in options ? options.deliveryAddress : orderDetails.deliveryAddress,
          deliveryEstimate: 'deliveryEstimate' in options ? options.deliveryEstimate : orderDetails.deliveryEstimate,
          deliveryAddressGeoCoordinates: 'deliveryAddressGeoCoordinates' in options ? options.deliveryAddressGeoCoordinates : orderDetails.deliveryAddressGeoCoordinates,
          expirationTime: 'expirationTime' in options ? options.expirationTime : orderDetails.expirationTime,
          fulfillmentTime: 'fulfillmentTime' in options ? options.fulfillmentTime : orderDetails.fulfillmentTime,
          fulfillmentType: 'fulfillmentType' in options ? options.fulfillmentType : orderDetails.fulfillmentType,
          groupMemberGuestId: 'groupMemberGuestId' in options ? options.groupMemberGuestId : orderDetails.groupMemberGuestId,
          menuType: 'menuType' in options ? options.menuType : orderDetails.menuType,
          orderType: 'orderType' in options ? options.orderType : orderDetails.orderType,
          tableNumber: 'tableNumber' in options ? options.tableNumber : orderDetails.tableNumber
        };

        await dispatch('session/updateOrderDetails', updatedOrderDetails, { root: true });

        if (options.location) {
          await dispatch('session/updateRouteParamsDirectly', { merchantLocationId: null }, { root: true });

          await dispatch('locations/selectLocation', {
            location: options.location,
            silent: 'silent' in options ? options.silent : true,
            withMenu: true
          }, { root: true });
        }

        if (!options.skipMenuTypeChange) {
          await dispatch('updateOrderMenuType');
        }
      }
      catch (error) {
        errorHandlers.ordering(error);

        throw error;
      }
    },

    async updateOrderMenuType({
      commit, dispatch, getters, rootState, rootGetters, state
    }) {
      try {
        const { order } = getters;
        const { menuType } = rootState.menu;
        const orderDetails = rootGetters['session/orderDetails'];

        if (
          state.orderMenuTypeIsUpdating
          || orderDetails.orderType === 'pay'
          || orderDetails.orderType === 'table'
          || !order.menuType
          || !menuType
          || (order.menuType === menuType && order.menuType !== 'Catering')
        ) {
          return;
        }
        commit('SET_ORDER_MENU_TYPE_UPDATING', true);

        const mappedFulfillmentType = mapToFulfillmentType(orderDetails.fulfillmentType);

        const requestBody = {
          fulfillmentType: mappedFulfillmentType,
          menuType
        };

        const response = await http.put(`/cardfree-api/v1/account/me/order/${order.orderId}/change-menu-type`, requestBody);

        bridge.triggerNewRelicEvent('UpdateOrderMenuType', 'Information', 'Order Menu-type Changed');

        await dispatch('updateOrderWithResponse', {
          order: response.data
        });

        commit('SET_ORDER_MENU_TYPE_UPDATING', false);
      }

      catch (error) {
        commit('SET_ORDER_MENU_TYPE_UPDATING', false);
        throw error;
      }
    },

    async updateGroupOrderWithResponse({ dispatch, getters }, options) {
      try {
        const { order } = getters;
        options.order = order;

        if (!order.groupOrder.you) {
          /*
            NOTE: This is an extreme edge case / bug that has occured in DEV before. This can happen
            if somehow the user's session.orderDetails are reset (losing their groupMemberGuestId)
            but their device still has a reference to the orderId. If we don't handle this case,
            the app would remain in a broken state, so it's a good idea to handle it.
          */

          events.emit('ordering-error', {
            customMessage: {
              title: 'Order Disconnect',
              description: 'You were disconnected from your group order. If you\'d like, you can start your own order.'
            }
          });

          return;
        }

        if (['Fulfilled', 'Future'].includes(order.status) && !order.groupOrder.you.isHost) {
          events.emit('group-ordering:fulfilled', order);

          events.emit('notification', {
            type: 'success',
            message: 'Your order has been submitted!'
          });

          dispatch('session/unsetOrder', {}, { root: true });

          return;
        }

        await dispatch('syncGroupOrderIntervalStart');
        await dispatch('updateLockedStatusWithGroupOrderResponse', options);
        await dispatch('updateLocationWithGroupOrderResponse', options);
        await dispatch('menu/getMenu', { silent: true }, { root: true });
      }

      catch (error) {
        throw error;
      }
    },

    async updateOrderWithResponse(
      { commit, dispatch, getters },
      { order, justJoinedGroup, validate, validateDayparts = false }
    ) {
      try {
        const previousOrder = JSON.parse(JSON.stringify(getters.order));

        commit('SET_ORDER', order);

        if (order.status === 'Cancelled') {
          events.emit('ordering-error', {
            customMessage: {
              title: 'Order Canceled',
              description: 'Your order has been canceled. If you\'d like, you can start a new order.'
            }
          });

          return;
        }

        if (validate) {
          dispatch('validateOrderItems', {
            validateDayparts
          });
        }

        dispatch('session/updateOrderId', order.orderId, { root: true });

        if (order.groupOrder) {
          await dispatch('updateGroupOrderWithResponse', { order, justJoinedGroup, previousOrder });
        }
      }

      catch (error) {
        throw error;
      }
    },

    updateStagedOrderItems({ commit, dispatch }, items) {
      storage.local.set('orderItemsStaged', items);

      commit('SET_STAGED_ORDER_ITEMS', items);

      dispatch('getUpsellItems');
    },

    async validateOrderItems({ dispatch, getters }, { validateDayparts }) {
      const { order } = getters;
      const itemsExceedingInventory = getItemsExceedingInventory(order);
      const invalidDaypartItems = getInvalidDaypartItems(order);
      const unavailableItems = getUnavailableItemsAndModifiers(order, true);

      if (validateDayparts && [...unavailableItems, ...invalidDaypartItems].length) {
        dispatch('handleUnavailableItems', { order, unavailableItems, invalidDaypartItems });
      }

      else if (unavailableItems.length) {
        dispatch('handleUnavailableItems', { order, unavailableItems });
      }

      if (itemsExceedingInventory.length) {
        dispatch('modals/openModal', {
          closeModal: 'menuItem',
          data: { items: itemsExceedingInventory },
          name: 'itemsExceedingInventory'
        }, { root: true });
      }
    }
  },

  mutations: {
    SET_ADD_ITEM_LOADING_STATUS(state, isAddingItem) {
      state.order.isAddingItem = isAddingItem;
    },

    SET_CACHED_ORDER_ID_SELECTION(state, orderId) {
      state.cachedOrderIdSelection = orderId;
    },

    SET_MENU_DATE(state, menuDate) {
      state.menuDate = menuDate;
    },

    SET_ORDER_MENU_TYPE_UPDATING(state, isUpdating) {
      state.orderMenuTypeIsUpdating = isUpdating;
    },

    SET_ORDER(state, order = {}) {
      state.order.applePassUrl = order.applePassUrl || null;
      state.order.attachmentUrl = order.attachmentUrl;
      state.order.discount = order.discount || 0;
      state.order.email = order.email || null;
      state.order.externalOrderId = order.externalOrderId || null;
      state.order.externalPaymentAmount = order.externalPaymentAmount || 0;
      state.order.features = order.features || null;
      state.order.fulfillmentType = order.fulfillmentType || null;
      state.order.googleWalletJwt = order.googleWalletJwt || null;
      state.order.groupOrder = order.groupOrder || null;
      state.order.locationType = order.locationType || null;
      state.order.locationTypeDescription = order.locationTypeDescription || null;
      state.order.loyaltyEngagementSummary = order.loyaltyEngagementSummary || null;
      state.order.menuType = order.menuType || null;
      state.order.offers = order.offers || [];
      state.order.orderAttributes = order.orderAttributes || {};
      state.order.orderFees = order.orderFees;
      state.order.orderFeeTotal = order.orderFeeTotal || 0;
      state.order.orderId = order.orderId ?? null;
      state.order.orderItems = order.orderItems || [];
      state.order.orderItemTotal = order.orderItemTotal || 0;
      state.order.orderLevelInstructions = order.orderLevelInstructions || null;
      state.order.orderState = order.orderState || null;
      state.order.orderType = order.orderType || null;
      state.order.payments = order.payments || [];
      state.order.phoneNumber = order.phoneNumber || null;
      state.order.posOrderStatus = order.posOrderStatus || null;
      state.order.preparationTime = order.preparationTime || null;
      state.order.redemptionQrCodeValue = order.redemptionQrCodeValue || null;
      state.order.serviceCharges = order.serviceCharges || 0;
      state.order.showRedemptionQrCode = order.showRedemptionQrCode || false;
      state.order.status = order.status || null;
      state.order.store = order.store || {
        address: {}
      };
      state.order.storeFees = order.storeFees || 0;
      state.order.subtotal = order.subtotal || 0;
      state.order.tableNumber = order.tableNumber || null;
      state.order.tableNumberDisplayName = order.tableNumberDisplayName || null;
      state.order.tax = order.tax || 0;
      state.order.ticketNumber = order.ticketNumber || 0;
      state.order.total = order.total || 0;
      state.order.totalWithTipAndCharges = order.totalWithTipAndCharges || 0;
      state.order.upsellItems = order.upsellItems;
      state.order.upsellRules = order.upsellRules || [];
      state.order.upsellDisplayMode = order.upsellDisplayMode;
      state.order.pickupDate = order.pickupDate;
    },

    SET_ORDER_ASSIGNING_STATUS(state, isAssigning) {
      state.order.isAssigning = isAssigning;
    },

    SET_ORDER_ERRORS(state, errors) {
      state.orderErrors = errors || [];
    },

    SET_STAGED_ORDER_ITEMS(state, items) {
      state.order.orderItemsStaged = items;
    },

    SET_ORDER_LOADING_STATUS(state, isLoading) {
      state.order.isLoading = isLoading;
    },

    SET_ORDERS(state, orders) {
      state.orders = orders || [];
    },

    SET_REMOVE_ITEM_LOADING_STATUS(state, options) {
      if (options.isStarting) {
        state.order.removingItemIds.push(...options.ids);
      }

      else {
        state.order.removingItemIds = state.order.removingItemIds.filter(id => !options.ids.includes(id));
      }
    },

    SET_REMOVE_OFFER_LOADING_STATUS(state, options) {
      if (options.isStarting) {
        state.order.removingOfferIds.push(options.id);
      }

      else {
        state.order.removingOfferIds = state.order.removingOfferIds.filter(id => options.id !== id);
      }
    },

    SET_UPSELL_ITEMS(state, payload) {
      state.order.upsellItems = payload.upsellItems || [];
      state.order.upsellDisplayMode = payload.defaultUpsellDisplayMode;
    },

    UNSET_ORDER(state) {
      state.menuDate = null;
      state.order.applePassUrl = null;
      state.order.attachmentUrl = null;
      state.order.discount = 0;
      state.order.email = null;
      state.order.externalOrderId = null;
      state.order.externalPaymentAmount = 0;
      state.order.fulfillmentType = null;
      state.order.googleWalletJwt = null;
      state.order.groupOrder = null;
      state.order.locationType = null;
      state.order.locationTypeDescription = null;
      state.order.loyaltyEngagementSummary = null;
      state.order.menuType = null;
      state.order.offers = [];
      state.order.orderAttributes = {};
      state.order.orderId = null;
      state.order.orderItems = [];
      state.order.orderItemsStaged = [];
      state.order.orderItemTotal = 0;
      state.order.orderLevelInstructions = null;
      state.order.orderState = null;
      state.order.orderType = null;
      state.order.payments = [];
      state.order.phoneNumber = null;
      state.order.posOrderStatus = null;
      state.order.preparationTime = null;
      state.order.redemptionQrCodeValue = null;
      state.order.status = null;
      state.order.store = {
        address: {}
      };
      state.order.serviceCharges = 0;
      state.order.orderFees = [];
      state.order.subtotal = 0;
      state.order.tableNumber = null;
      state.order.tableNumberDisplayName = null;
      state.order.tax = 0;
      state.order.ticketNumber = null;
      state.order.total = 0;
      state.order.totalWithTipAndCharges = 0;
      state.order.upsellItems = [];
    },

    SET_TDS_DATA(state, data) {
      if (data) {
        const {
          threeDsTransactionId,
          serverTransactionId = uuid(),
          programProtocol,
          cavv,
          eciIndicator,
          status
        } = data;

        state.order.tdsData = {
          type: 'D',
          status,
          threeDsTransactionId,
          serverTransactionId,
          programProtocol,
          cavv,
          eciIndicator
        };
      }
      else {
        state.order.tdsData = null;
      }
    },

    SET_CLOVER_BROWSER_INFO(state, browserInfo = null) {
      state.order.cloverBrowserInfo = browserInfo || null;
    }
  }
};
